import { Component, ViewEncapsulation, input, output, ChangeDetectionStrategy, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import replace from 'lodash-es/replace';

export interface SuggestionMessage {
  body: any;
  messageId: string;
  createdOn: Date;
  isStartOfDay: boolean;
  suggestions?: any;
  archived: boolean;
  suggestFaqAnswersResponse?: any;
  suggestArticlesResponse?: any;
  articleExpanded?: boolean;
  faqExpanded?: boolean;
}

@Component({
  selector: 'chat-suggestion-item',
  templateUrl: './chat-suggestion-item.component.html',
  styleUrls: ['./chat-suggestion-item.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChatSuggestionItemComponent {
  locale = input('en');
  item = input.required<SuggestionMessage>();
  archive = output<void>();
  loadSuggestionAttachment = output<void>();
  goTo = output<void>();
  send = output<any>();
  feedback = output<any>();
  opened = output<string>();

  public showFeedback = signal(true);
  private sourceForFeedback = signal<any>(null);

  constructor(public translateService: TranslateService) {}

  public get Id() {
    const suffix = this.item().archived ? '-archive' : '-active';
    return this.item().body.parentMessageId + suffix;
  }

  public isToday(date): boolean {
    const currentDate = new Date();
    const setDate = new Date(date);
    if (
      setDate.getDate() === currentDate.getDate() &&
      setDate.getMonth() === currentDate.getMonth() &&
      setDate.getFullYear() === currentDate.getFullYear()
    ) {
      return true;
    }
    return false;
  }

  public archiveSuggestion() {
    this.archive.emit();
  }

  public loadSuggestionsData() {
    if (!this.item().suggestArticlesResponse && !this.item().suggestFaqAnswersResponse) {
      this.loadSuggestionAttachment.emit();
    }
  }

  public goToMessage() {
    this.goTo.emit();
  }

  public openArticle(article: { uri: string }) {
    window.open(replace(article.uri, 'gs://', 'https://storage.googleapis.com/'));
  }

  public showNext(data, type) {
    data.selectedIndex++;

    if (type === 'article') {
      if (data.selectedIndex >= data.articleAnswersList.length) {
        data.selectedIndex = data.articleAnswersList.length - 1;
      }
      for (let index = 0; index < data.articleAnswersList.length; index++) {
        data.articleAnswersList[index].selected = index === data.selectedIndex ? true : false;
      }
    }

    if (type === 'faq') {
      if (data.selectedIndex >= data.faqAnswersList.length) {
        data.selectedIndex = data.faqAnswersList.length - 1;
      }
      for (let index = 0; index < data.faqAnswersList.length; index++) {
        data.faqAnswersList[index].selected = index === data.selectedIndex ? true : false;
      }
    }
  }

  public showLast(data, type) {
    if (type === 'article') {
      data.selectedIndex = data.articleAnswersList.length - 1;
      for (let index = 0; index < data.articleAnswersList.length; index++) {
        data.articleAnswersList[index].selected = index === data.selectedIndex ? true : false;
      }
    }

    if (type === 'faq') {
      data.selectedIndex = data.faqAnswersList.length - 1;
      for (let index = 0; index < data.faqAnswersList.length; index++) {
        data.faqAnswersList[index].selected = index === data.selectedIndex ? true : false;
      }
    }
  }

  public showFirst(data, type) {
    data.selectedIndex = 0;

    if (type === 'article') {
      for (let index = 0; index < data.articleAnswersList.length; index++) {
        data.articleAnswersList[index].selected = index === data.selectedIndex ? true : false;
      }
    }

    if (type === 'faq') {
      for (let index = 0; index < data.faqAnswersList.length; index++) {
        data.faqAnswersList[index].selected = index === data.selectedIndex ? true : false;
      }
    }
  }

  public showPrevious(data, type) {
    data.selectedIndex--;

    if (type === 'article') {
      if (data.selectedIndex <= 0) {
        data.selectedIndex = 0;
      }
      for (let index = 0; index < data.articleAnswersList.length; index++) {
        data.articleAnswersList[index].selected = index === data.selectedIndex ? true : false;
      }
    }

    if (type === 'faq') {
      if (data.selectedIndex <= 0) {
        data.selectedIndex = 0;
      }
      for (let index = 0; index < data.faqAnswersList.length; index++) {
        data.faqAnswersList[index].selected = index === data.selectedIndex ? true : false;
      }
    }
  }

  public setSourceForFeedback(source) {
    this.sourceForFeedback.set(source);
  }

  public sendArticle(article) {
    this.send.emit({
      action: 'sendArticle',
      data: article
    });
  }

  public sendSnippet(snippet, article) {
    this.send.emit({
      action: 'sendSnippet',
      data: {
        snippet,
        article
      }
    });
  }

  public sendFaq(faq) {
    this.send.emit({
      action: 'sendFaq',
      data: faq
    });
  }

  public feedbackAnswer(feedbackAnswer) {
    this.feedback.emit({
      answer: feedbackAnswer,
      data: this.sourceForFeedback()
    });

    this.showFeedback.set(false);
    setTimeout(() => {
      this.showFeedback.set(true);
    }, 0);
  }

  public onOpened(source) {
    this.opened.emit(source);
  }

  public onArticlePanelChange(value: any) {
    if (value != null) {
      this.loadSuggestionsData();
      this.onOpened('article');
    }
  }

  public onFaqPanelChange(value: any) {
    if (value != null) {
      this.loadSuggestionsData();
      this.onOpened('faq');
    }
  }
}
